import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import DataTable from "react-data-table-component";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { getAllIotJobs } from "../commonApi";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faList, faChartPie } from '@fortawesome/free-solid-svg-icons';
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { Loader } from "../Loader";

const renderTooltip = (props) => (
    <Tooltip {...props}>{props.title}</Tooltip>
);

function IotJobsGrid() {
    const navigate = useNavigate();

    // Pagination + sorting
    const getInitialPage = () => {
        const saved = localStorage.getItem("pagination-page-iot-jobs");
        return saved ? parseInt(saved, 10) - 1 : 0; // 0-based for API
    };

    const getInitialPerPage = () => {
        const saved = localStorage.getItem("pagination-perPage-iot-jobs");
        return saved ? parseInt(saved, 10) : 10;
    };

    const [page, setPage] = useState(getInitialPage());
    const [countPerPage, setCountPerPage] = useState(getInitialPerPage());
    const [sortOrder, setSortOrder] = useState("desc");
    const [sortColumn, setSortColumn] = useState("jobId");
    const [searchTerm, setSearchTerm] = useState("");
    const [jobStatusData, setJobStatusData] = useState([]);
    const [totalRows, setTotalRows] = useState(0);
    const [loading, setLoading] = useState(true);
    const resetTriggerRef = useRef(0);

    useEffect(() => {
        let isCancelled = false;
        const fetchJobStatus = async () => {
            if (isCancelled) return;
            setLoading(true);
            try {
                const params = {
                    pageNo: page,
                    pageSize: countPerPage,
                    sortBy: sortColumn,
                    sortOrder,
                    searchValue: searchTerm || undefined,
                };

                const response = await getAllIotJobs(params);
                const jobs = (response?.data?.content || []).filter(
                    (item) => item && typeof item === "object" && item.jobId
                );
                setJobStatusData(jobs);
                setTotalRows(response?.data?.totalElements ?? 0);
            } catch (error) {
                toast.error(error?.response?.data?.error?.message || "Error fetching Job Status!");
                setJobStatusData([]);
            } finally {
                setLoading(false);
            }
        };

        const delayDebounceFn = setTimeout(fetchJobStatus, 500);
        return () => {
            isCancelled = true;
            clearTimeout(delayDebounceFn);
        };
    }, [page, countPerPage, sortColumn, sortOrder, searchTerm]);

    const handleSearchInputChange = (event) => {
        setSearchTerm(event.target.value);
        setPage(0);
        localStorage.setItem("pagination-page-iot-jobs", "1");
        resetTriggerRef.current += 1;
    };

    const handlePerRowsChange = (newPerPage, newPage) => {
        setCountPerPage(newPerPage);
        setPage(newPage - 1);
        localStorage.setItem("pagination-perPage-iot-jobs", newPerPage.toString());
        localStorage.setItem("pagination-page-iot-jobs", newPage.toString());
        resetTriggerRef.current += 1;
    };

    const handlePageChange = (newPage) => {
        const newIndex = newPage - 1;
        if (newIndex === page || newIndex < 0) return;
        setPage(newIndex);
        localStorage.setItem("pagination-page-iot-jobs", newPage.toString());
        resetTriggerRef.current += 1;
    };

    const handleSort = (column, sortDirection) => {
        setSortColumn(column.selector || "jobId");
        setSortOrder(sortDirection);
        setPage(0);
        localStorage.setItem("pagination-page-iot-jobs", "1");
        resetTriggerRef.current += 1;
    };

    const columns = [
        { name: "Job Id", selector: (row) => row?.jobId || "—", sortable: true, width: "275px" },
        { name: "Group Name", selector: (row) => row?.deviceGroupName || "—", sortable: true, width: "280px" },
        { name: "Operation", selector: (row) => row?.operation || "—", sortable: true, width: "280px" },
        { name: "Status", selector: (row) => row?.status || "—", sortable: true, width: "280px" },
        {
            name: "Actions",
            cell: (row) => (
                <div style={{ display: "flex", gap: "10px" }}>
                    <OverlayTrigger
                        placement="top"
                        overlay={<Tooltip id={`tooltip-list-${row.jobId}`}>Device Details</Tooltip>}
                    >
                        <button
                            className="btn"
                            style={{ backgroundColor: "#4340d2", border: "1px solid #ccc" }}
                            onClick={() => navigate(`/list/${row.jobId}`)}
                        >
                            <FontAwesomeIcon icon={faList} style={{ color: "white" }} />
                        </button>
                    </OverlayTrigger>
                    <OverlayTrigger
                        placement="top"
                        overlay={<Tooltip id={`tooltip-dashboard-${row.jobId}`}>Dashboard</Tooltip>}
                    >
                        <button
                            className="btn"
                            style={{ backgroundColor: "#4340d2", border: "1px solid #ccc" }}
                            onClick={() => navigate(`/otaDashboard/${row.jobId}`)}
                        >
                            <FontAwesomeIcon icon={faChartPie} style={{ color: "white" }} />
                        </button>
                    </OverlayTrigger>
                </div>
            ),
            ignoreRowClick: true,
            allowOverflow: true,
            button: true,
        },
    ];

    return (
        <div className="custom_tab_wrapper">
            <ul className="nav nav-tabs" id="myTab" role="tablist">
                <li className="nav-item" role="presentation">
                    <button
                        className="nav-link active"
                        id="firmware-tab"
                        data-bs-toggle="tab"
                        data-bs-target="#firmware"
                        type="button"
                        role="tab"
                        aria-controls="firmware"
                        aria-selected="true"
                    >
                        IoT Jobs: {totalRows}
                    </button>
                </li>
            </ul>
            <div className="tab-pane fade show active" id="firmware" role="tabpanel">
                <div className="tab_content_wrapper">
                    <div className="d-flex justify-content-between mb-2 custom_table_action">
                        <input
                            className="form-control w-25"
                            type="text"
                            placeholder="Search by Name"
                            value={searchTerm}
                            onChange={handleSearchInputChange}
                        />
                    </div>
                    <div className="table-responsive custom_table">
                        {loading ? (
                            <div className="spin-loader">
                                <Loader />
                            </div>
                        ) : (
                            <DataTable
                                key={`datatable-iot-jobs-${page + 1}-${countPerPage}-${resetTriggerRef.current}`}
                                columns={columns}
                                data={jobStatusData}
                                pagination
                                paginationServer
                                paginationTotalRows={totalRows}
                                progressPending={loading}
                                paginationDefaultPage={page + 1}
                                paginationPerPage={countPerPage}
                                onChangePage={handlePageChange}
                                onChangeRowsPerPage={handlePerRowsChange}
                                onSort={handleSort}
                                sortServer
                                highlightOnHover
                                fixedHeader
                                fixedHeaderScrollHeight="440px"
                                paginationComponentOptions={{
                                    rowsPerPageText: "Rows per page:",
                                    rangeSeparatorText: "of",
                                    noRowsPerPage: false,
                                    selectAllRowsItem: false,
                                    rowsPerPageOptions: [10, 15, 20, 25, 50],
                                }}
                                paginationResetDefaultPage={resetTriggerRef.current > 0}
                            />
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default IotJobsGrid;
